import Vue from "vue";
import { googleLogin, microsoftLogin, signIn, myInfo, signOut, signUp } from "@/apis/auth";
import { REGISTRATION_TYPE, SETTING } from "@/constants/config";
import { EMAIL_VERIFICATION_ERROR } from '@/constants/errors';
import Company from "@/models/Company";
import { cleanUpGuestSessionDataAndLogGuessOut, isInSignAllPage, prepareAuthenticatedUserSigningSessionData, prepareGuestSigningSessionData } from "./auth-helper";

export default {
    namespaced: true,

    state: {
        user: null,
        // is login
        authenticated: false,

        verified: false,

        // is finished all onboarding steps
        onboarded: false,

        // has paid ?
        subscribed: false,

        // intended route name
        intendedRouteName: null,
        switchTo: null,

        // control admin visible menu
        actAs: 'user',
        
        isTokenExpired: false,
        
        expiredTokenData: null,
        redirectToPasswordReset: false
    },

    getters: {
        authenticated(state) {
            return state.authenticated;
        },

        onboarded(state)
        {
            return state.onboarded;
        },

        subscribed(state)
        {
            return state.subscribed;
        },

        user(state) {
            return state.user;
        },

        isAdmin(state)
        {
            return state.user && state.user.is_secretary_admin;
        },

        verified(state)
        {
            return state.verified;
        },

        intendedRouteName(state)
        {
            return state.intendedRouteName;
        },

        switchTo(state)
        {
            return state.switchTo;
        },

        actAs(state)
        {
            return state.actAs;
        },

        tokenExpiration(state)
        {
            return state.isTokenExpired;
        },

        expiredTokenData(state)
        {
            return state.expiredTokenData;
        },
        
        redirectToPasswordReset(state)
        {
            return state.redirectToPasswordReset;
        }
    },

    mutations: {
        SET_AUTHENTICATED(state, value) {
            state.authenticated = value;
        },

        SET_USER(state, value) {
            state.user = value;
        },

        UPDATE_ONBOARDING_STATUS(state, value)
        {
            state.onboarded = value;
        },

        UPDATE_SUBSCRIBE_STATUS(state, value)
        {
            state.subscribed = value;
        },

        UPDATE_VERIFIED_STATUS(state, value)
        {
            state.verified = value;
        },

        CLEAR_USER_DATA(state) {
            state.user = null;
        },

        SET_INTENDED_ROUTE_NAME(state, url)
        {
            state.intendedRouteName = url;
        },

        SET_SWITCH_TO(state, id)
        {
            state.switchTo = id;
        },

        SET_ACT_AS(state, as)
        {
            state.actAs = as;
        },
        
        SET_TOKEN_EXPIRATION(state, value)
        {
            state.isTokenExpired = value;
        },

        SET_EXPIRED_DATA(state, value)
        {
            state.expiredTokenData = value;
        },


        UPDATE_USER_EMAIL(state, value)
        {
            state.user.email = value;
        },
        
        SET_REDIRECT_TO_PASSWORD_RESET(state, value)
        {
            state.redirectToPasswordReset = value;
        }
    },

    actions: {
        async login({ dispatch }, credentials) {
            await signIn(credentials);
            
            return dispatch("me");
        },

        async logout() {
            await signOut();
        },

        async register( { dispatch },credentials) {
            await signUp(credentials);

            return dispatch("me");
        },

        async me({ commit, rootGetters, getters, state }) {
            const searchParam = new URLSearchParams(window.location.search);
            const switchCompanyTo = searchParam.get('switch-to');
            
            if (switchCompanyTo) {
                commit('SET_SWITCH_TO', switchCompanyTo);
            }

            // cannot use router here, due to cycle dependency
            let type = searchParam.get('type');
            if (!type) type = rootGetters['company/registrationType'];

            // * if the registration type exists this mean user (non-admin) want to create a new company
            // * so we need to set the active company (backend session id) to 0 which will handle rest
            if (type && REGISTRATION_TYPE.TYPES.includes(type))
            {
                commit('company/SET_REGISTRATION_TYPE', type, { root: true });
                await Company.setOnboardingState();
            }

            const companyId = getters.switchTo;

            const path = window.location.pathname.split("/");
            // Let Users Do Anything Without Creating Account = LUDAWCA
            const LUDAWCA = searchParam.get('LUDAWCA');
            const isVerifiedUser = searchParam.get('is_verified_user');

            /** We allow guest signing, only in sign all page */
            if (isInSignAllPage(path)) {
                if (LUDAWCA) {
                    await prepareGuestSigningSessionData(LUDAWCA, isVerifiedUser, commit);
                } else {
                    prepareAuthenticatedUserSigningSessionData(searchParam.get('company_process_id'), commit);
                }
            } else if (Vue.prototype.$cache.get('redirect-to-password-reset')) {
                await cleanUpGuestSessionDataAndLogGuessOut(commit);
            } 

            const response = await myInfo(companyId).then(async data => {
                if (companyId) {
                    commit('SET_SWITCH_TO', null);
                }

                commit("SET_AUTHENTICATED", true);
                commit("SET_USER", data.user);

                let userCompanies = [];
                
                if (data.user.is_secretary_admin) {
                    commit("UPDATE_ONBOARDING_STATUS", true);
                    commit("UPDATE_SUBSCRIBE_STATUS", true);
                    userCompanies = data.companies;
                } else {
                    userCompanies = data.user.companies;
                }
              
                commit("company/UPDATE_USER_COMPANIES", userCompanies, { root: true });
                if (data.active_company?.settings)
                {
                    commit("company/UPDATE_USER_COMPANIES", data.companies, { root: true });
                    const {settings} = data.active_company;

                    // get level
                    const level = settings?.find(setting => setting.key === SETTING.LEVEL);

                    if (level?.value !== SETTING.LEVEL_0) {
                        commit('company/UPDATE_CURRENT_COMPANY', data.active_company, { root: true });
                        commit('company/SET_ACTIVE_COMPANY_STATUS', data.active_company.status, { root: true });
                    }

                    if (level?.value === SETTING.LEVEL_1) commit("UPDATE_SUBSCRIBE_STATUS", true);

                    // checkout constant/config.js/SETTING for levels
                    if ((level?.value === SETTING.LEVEL_2)) commit("UPDATE_ONBOARDING_STATUS", true);

                    if (data.user.email_verified_at) {
                        commit('UPDATE_VERIFIED_STATUS', true);
                    }
                }

                if (state.onboarded) {
                    commit('appConfig/UPDATE_NAV_MENU_HIDDEN', false, { root: true });
                } else {
                    commit('appConfig/UPDATE_NAV_MENU_HIDDEN', true, { root: true });
                }
            })
                .catch(e => {
                    if (e.response?.data?.error === EMAIL_VERIFICATION_ERROR) {
                        commit("SET_AUTHENTICATED", true);
                        commit('UPDATE_VERIFIED_STATUS', false);
                    } else {
                        commit("SET_AUTHENTICATED", false);
                        commit("SET_USER", null);
                        commit("company/UPDATE_USER_COMPANIES", [], { root: true });
                    }

                    return e.response;
                });

            return response;
        },

        async googleLogin({dispatch}, credentials){
            await googleLogin(credentials);

            return dispatch("me");
        },

        async microsoftLogin({dispatch}, credentials){
            await microsoftLogin(credentials);

            return dispatch("me");
        }
    },
};
